/**
 * @file     rentMortgage.ts
 * @purpose  Single source of truth for the rent-vs-mortgage family: reverse a
 *           monthly rent into the purchase price it would carry, given a down
 *           payment and approved rate, so the server-rendered pages, the
 *           calculator and PropertyCard all agree to the dollar. Every price on
 *           a page must derive from one call here and one rounding step.
 */
import { POSTED_FIVE_YEAR_RATE_PCT, amortizedPayment, principalForPayment } from '@/core/finance/mortgage';

export interface MortgageAssumptions {
  /** Annual interest rate, as a percentage (5.19 = 5.19%). */
  rate: number;
  /** Down payment, as a percentage of purchase price (20 = 20%). */
  downPct: number;
  /** Amortization in years. */
  amortYears: number;
}

/**
 * Posted five-year fixed (POSTED_FIVE_YEAR_RATE) / 20% down / 25-year
 * amortization. PropertyCard reads this object, so the per-card `~$X/mo` line
 * and this family's headline number reconcile.
 */
export const DEFAULT_ASSUMPTIONS: MortgageAssumptions = {
  rate: POSTED_FIVE_YEAR_RATE_PCT,
  downPct: 20,
  amortYears: 25,
};

/**
 * Carrying-cost model layered on top of the mortgage payment. Property tax is
 * the one figure that varies by municipality, so callers that know the city
 * pass the real BC Schedule 702 residential rate (see market-data/tax); strata
 * and insurance stay disclosed estimates (labelled as such on every page and in
 * the AssumptionsPanel) because neither is knowable from rent alone. The engine
 * is pure, so the model is injected rather than read here.
 */
export interface CarryingModel {
  /** Combined residential property tax, dollars per $1,000 of value. */
  taxRatePer1000: number;
  /** Disclosed monthly strata estimate. */
  strataMonthly: number;
  /** Disclosed monthly home-insurance estimate. */
  insuranceMonthly: number;
}

/**
 * Blended BC defaults, used province-wide and by the client calculator (which
 * cannot reach the DB). 3.5 per $1,000 = ~0.35%, mid-range for BC residential.
 */
export const DEFAULT_CARRYING: CarryingModel = {
  taxRatePer1000: 3.5,
  strataMonthly: 320,
  insuranceMonthly: 62,
};
/** Floor so a rent fully consumed by carrying still resolves to a real price. */
const MIN_PAYMENT = 200;

function carryingMonthly(rawMortgagePrice: number, model: CarryingModel): number {
  const monthlyTax = (rawMortgagePrice * (model.taxRatePer1000 / 1000)) / 12;
  return Math.round(monthlyTax) + model.strataMonthly + model.insuranceMonthly;
}

/** Round any price figure to the nearest $1,000. The one rounding step. */
export function roundPrice(price: number): number {
  return Math.round(price / 1000) * 1000;
}

/** Purchase price whose mortgage payment equals `payment`, before rounding. */
function priceForPayment(payment: number, a: MortgageAssumptions): number {
  const n = a.amortYears * 12;
  const principal = principalForPayment(payment, a.rate / 100, n);
  return principal / (1 - a.downPct / 100);
}

/**
 * Monthly mortgage payment for a purchase price. Inverse of `priceForPayment`;
 * this is the figure PropertyCard shows and the "Under your rent" chip tests.
 */
export function monthlyPayment(price: number, a: MortgageAssumptions = DEFAULT_ASSUMPTIONS): number {
  const n = a.amortYears * 12;
  const principal = price * (1 - a.downPct / 100);
  return Math.round(amortizedPayment(principal, a.rate / 100, n));
}

export interface RentQualification {
  /** Rent that produced this, echoed back for display. */
  rent: number;
  assumptions: MortgageAssumptions;
  /** Headline: the price whose *mortgage payment* equals the rent. Rounded. */
  mortgagePrice: number;
  /** The mortgage principal at `mortgagePrice` (price minus down payment). */
  mortgagePrincipal: number;
  /** Estimated monthly tax + strata + insurance at `mortgagePrice`. */
  carrying: number;
  /** The carrying model these figures were computed from (for disclosure). */
  carryingModel: CarryingModel;
  /** Honest number: price whose mortgage + carrying together equal the rent. */
  allInPrice: number;
  /** Down payment in dollars at `mortgagePrice`. */
  downPayment: number;
}

/**
 * Reverse a monthly rent into the price it carries. Callers derive H1, intro,
 * stat rail, the math table and the listing price ceiling from this one object.
 */
export function qualifyFromRent(
  rent: number,
  assumptions: MortgageAssumptions = DEFAULT_ASSUMPTIONS,
  carryingModel: CarryingModel = DEFAULT_CARRYING,
): RentQualification {
  const rawMortgagePrice = priceForPayment(rent, assumptions);
  const mortgagePrice = roundPrice(rawMortgagePrice);
  const carrying = carryingMonthly(rawMortgagePrice, carryingModel);
  const allInPrice = roundPrice(priceForPayment(Math.max(rent - carrying, MIN_PAYMENT), assumptions));
  const downPayment = roundPrice(mortgagePrice * (assumptions.downPct / 100));
  const mortgagePrincipal = mortgagePrice - downPayment;
  return { rent, assumptions, mortgagePrice, mortgagePrincipal, carrying, allInPrice, downPayment, carryingModel };
}
